/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, Text, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorBlock } from '../../components/ConsultaSeguimentoIndicatorBlock'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { ExemplosVerticalField, ExemplosVerticalFieldOptions } from '../../components/ExemplosVerticalField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta16ExemplosEnum,
  ConsultaSeguimentoPergunta16Model,
} from './model-consultaSeguimentoPergunta16'
import {
  hasPergunta16ExemplosFalhouAnySim,
  hasPergunta16ExemplosPassouAnySim,
  isPergunta16ExemplosAllFilled,
  isPergunta16ExemplosAllNao,
} from './utils-consultaSeguimentoPergunta16'

interface ConsultaSeguimentoPergunta16FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta16Model>
}

export function ConsultaSeguimentoPergunta16Field(props: Readonly<ConsultaSeguimentoPergunta16FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta16Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta16Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_16_HEADER === SimNaoEnum.SIM
  const isPerguntaExemplosAllNao = isPergunta16ExemplosAllNao(value?.PERGUNTA_16_EXEMPLOS)
  const hasPerguntaExemplosPassouAnySim = hasPergunta16ExemplosPassouAnySim(value?.PERGUNTA_16_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta16ExemplosFalhouAnySim(value?.PERGUNTA_16_EXEMPLOS)
  const hasSimOnlyInExemplosPassou = hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim
  const hasSimOnlyInExemplosFalhou = hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim
  const textoBasePassou = hasSimOnlyInExemplosPassou
    ? 'Sim somente para exemplos Passou:'
    : 'Os mais frequentes são exemplos de Passou:'

  const textResultadoFalhou = hasSimOnlyInExemplosFalhou
    ? 'Sim somente para exemplos Falhou:'
    : 'Os mais frequentes são exemplos de Falhou:'
  const textResultadoPassou = isPerguntaHeaderSim ? '' : textoBasePassou

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_16}
          title={
            <ConsultaSeguimentoPerguntaHeaderTitle
              prefix='Se você virar a cabeça para olhar para alguma coisa,'
              sufix=' olha ao redor para ver o que você está olhando?'
            />
          }
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_16_HEADER} />}
        />
        {!isPerguntaHeaderSim && !isEmpty(value.PERGUNTA_16_HEADER) && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              <ConsultaSeguimentoIndicatorHelper
                title='O que ele faz quando você se vira para olhar para alguma coisa?'
                subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
              />
            </VFlow>
            <VFlow vSpacing={0}>
              <ExemplosVerticalField<ConsultaSeguimentoPergunta16ExemplosEnum>
                name={name.PERGUNTA_16_EXEMPLOS}
                label='Responda todos os exemplos, conforme as respostas do genitor'
                options={PERGUNTA_16_EXEMPLOS_OPTIONS}
                title='Seu filho...'
                required
              />
              {isPerguntaExemplosAllNao && <ConsultaSeguimentoIndicatorBlock />}
              {hasPerguntaExemplosPassouAnySim &&
                hasPerguntaExemplosFalhouAnySim &&
                isPergunta16ExemplosAllFilled(value.PERGUNTA_16_EXEMPLOS) && (
                  <VFlow vSpacing={0}>
                    <ConsultaSeguimentoIndicatorStep
                      text={
                        <VFlow vSpacing={0}>
                          <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                          <Heading level={3} color='inherit'>
                            Pergunta sobre a frequência
                          </Heading>
                        </VFlow>
                      }
                      style={styles.indicatorStep}
                    />
                    <RadioGroupBoxField
                      name={name.PERGUNTA_16_FREQUENCIA}
                      label='Quais os que ele faz com mais frequência?'
                      options={PERGUNTA_16_FREQUENCIA_OPTIONS}
                      required
                    />
                  </VFlow>
                )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU: textResultadoFalhou,
            PASSOU: textResultadoPassou,
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta16Model): boolean => {
  if (values.PERGUNTA_16_HEADER === SimNaoEnum.SIM) {
    return true
  }

  if (
    !isPergunta16ExemplosAllFilled(values.PERGUNTA_16_EXEMPLOS) ||
    isPergunta16ExemplosAllNao(values.PERGUNTA_16_EXEMPLOS)
  ) {
    return false
  }

  const hasPerguntaExemplosPassouAnySim = hasPergunta16ExemplosPassouAnySim(values.PERGUNTA_16_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta16ExemplosFalhouAnySim(values.PERGUNTA_16_EXEMPLOS)

  if (
    (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) ||
    (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim)
  ) {
    return true
  } else {
    return !isEmpty(values.PERGUNTA_16_FREQUENCIA)
  }
}

const calculateResultado = (values: ConsultaSeguimentoPergunta16Model): PassouFalhouEnum => {
  if (values.PERGUNTA_16_HEADER === SimNaoEnum.SIM) {
    return PassouFalhouEnum.PASSOU
  }

  const hasPerguntaExemplosPassouAnySim = hasPergunta16ExemplosPassouAnySim(values.PERGUNTA_16_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta16ExemplosFalhouAnySim(values.PERGUNTA_16_EXEMPLOS)

  if (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) {
    return PassouFalhouEnum.PASSOU
  } else if (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim) {
    return PassouFalhouEnum.FALHOU
  } else {
    return values.PERGUNTA_16_FREQUENCIA
  }
}

const PERGUNTA_16_EXEMPLOS_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta16ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Olha para o que você está olhando?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.OLHA_PARA_O_QUE_VOCE_ESTA_OLHANDO,
    },
    {
      label: 'Aponta para o que você está olhando?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.APONTA_PARA_O_QUE_VOCE_ESTA_OLHANDO,
    },
    {
      label: 'Olha ao redor para ver o que você está olhando?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.OLHA_AO_REDOR_PARA_O_QUE_VOCE_ESTA_OLHANDO,
    },
  ],
  FALHOU: [
    {
      label: 'Não repara em você?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.NAO_REPARA_EM_VOCE,
    },
    {
      label: 'Olha para o seu rosto?',
      value: ConsultaSeguimentoPergunta16ExemplosEnum.OLHA_PARA_SEU_ROSTO,
    },
  ],
}

const PERGUNTA_16_FREQUENCIA_OPTIONS: RadioOption<PassouFalhouEnum>[] = [
  {
    label: 'Exemplos PASSOU',
    value: PassouFalhouEnum.PASSOU,
  },
  {
    label: 'Exemplos FALHOU',
    value: PassouFalhouEnum.FALHOU,
  },
]

const styles = {
  indicatorStep: css`
    width: auto;
  `,
}
